import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Download } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/Badge';
import Loader from '@/components/Loader';
import Pagination from '@/components/Pagination';
import { workReportService, userService, projectService } from '@/services';
import { fmtDate, download } from '@/utils/format';

export default function AdminWorkReports() {
  const token = useSelector((s) => s.auth.token);
  const [filters, setFilters] = useState({
    from: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    to: dayjs().format('YYYY-MM-DD'),
    user: '',
    project: '',
  });
  const [page, setPage] = useState(1);

  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => userService.list({ limit: 200 }) });
  const projects = useQuery({ queryKey: ['projects', 'all'], queryFn: () => projectService.list({ limit: 200 }) });
  const { data, isLoading } = useQuery({
    queryKey: ['work-reports', 'admin', filters, page],
    queryFn: () => workReportService.adminList({ ...filters, page, limit: 15 }),
  });

  const exportCSV = () => {
    const qp = new URLSearchParams(filters).toString();
    download(`reports/admin/work-reports.csv?${qp}`, `work_reports_${filters.from}_${filters.to}.csv`, token);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Work Reports"
        subtitle="What each employee is working on"
        actions={<button className="btn-secondary" onClick={exportCSV}><Download size={14} /> Export CSV</button>}
      />

      <Card>
        <div className="flex flex-wrap gap-2 mb-4">
          <div><label className="label">From</label><input type="date" className="input w-44" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} /></div>
          <div><label className="label">To</label><input type="date" className="input w-44" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} /></div>
          <div>
            <label className="label">Employee</label>
            <select className="input w-56" value={filters.user} onChange={(e) => setFilters((f) => ({ ...f, user: e.target.value }))}>
              <option value="">All</option>
              {(users.data?.items || []).map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Project</label>
            <select className="input w-56" value={filters.project} onChange={(e) => setFilters((f) => ({ ...f, project: e.target.value }))}>
              <option value="">All</option>
              {(projects.data?.items || []).map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </div>
        </div>

        {isLoading ? (
          <Loader />
        ) : (
          <div className="space-y-3">
            {(data?.items || []).length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No reports found</div>}
            {(data?.items || []).map((r) => (
              <div key={r._id} className="border border-slate-200 dark:border-slate-800 rounded-xl p-4">
                <div className="flex flex-wrap justify-between gap-2 mb-2">
                  <div>
                    <div className="font-semibold">{r.title}</div>
                    <div className="text-xs text-slate-500">
                      {fmtDate(r.date)} · {r.user?.name} · {r.project?.name || 'No project'} {r.task?.title ? `· ${r.task.title}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={r.status} />
                    <span className="text-xs text-slate-500">{r.hoursWorked || 0}h · {r.completionPercent || 0}%</span>
                  </div>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{r.description}</div>
              </div>
            ))}
          </div>
        )}
        <Pagination page={page} limit={15} total={data?.total || 0} onChange={setPage} />
      </Card>
    </div>
  );
}
